/**
 * Cliente das rotas do WhatsApi por organização (spec 2026-10-23 §5.2): servem a
 * organização que tem gateway e não tem tenant. Mensagens e estatísticas têm a mesma
 * forma das rotas de tenant, então os tipos são os de `whatsapi.ts` e `whatsapiStats.ts`.
 */

import { api } from './client'
import type {
  WaMessage, WaMessageListParams, WaMessageListResponse, WaSendPayload,
} from './whatsapi'
import { estatisticasEm, type EstatisticasWhatsApi } from './whatsapiStats'

/** Organização em que o usuário tem `whatsapi:read`, com o que ele pode nela. `create`,
 *  `update` e `delete` valem para o KB das chaves; `atender`, para as conversas. */
export interface OrgWhatsApi {
  id: number
  name: string
  pode: {
    read: boolean; observe: boolean; send: boolean
    create: boolean; update: boolean; delete: boolean
    atender: boolean
  }
}

/** Chave da organização (sem a key). Em `/chaves` vêm só as ativas e apontadas para um
 *  gateway; em `/api-keys`, todas. */
export interface ChaveDaOrg {
  id: string
  name: string
  key_prefix: string
  is_active: boolean
  gateway_id: string | null
  gateway_legado: string | null
  last_used_at: string | null
}

/** Corpo do envio por organização: o do tenant, sem `source` e com `channel`. */
export type EnvioDaOrg = Omit<WaSendPayload, 'source'> & { channel?: string }

/** Raiz das rotas do WhatsApi da organização (mesma forma das de tenant, `raizDoTenant`). */
export const raizDaOrg = (orgId: number) => `/v1/organizations/${orgId}/whatsapi`

/** Estado da conversa (spec 2026-11-02 conversas §5.3): `aguardando` é o transbordo do bot
 *  sem ninguém atendendo; `em_atendimento`, assumida por uma pessoa. */
export type EstadoDaConversa = 'bot' | 'aguardando' | 'em_atendimento'

export interface ConversaWhatsApp {
  gateway_id: string
  gateway_nome: string
  /** Identificador opaco da conversa (16 hex); o número em claro nunca sai do WhatsApi. */
  ref: string
  numero: string
  estado: EstadoDaConversa
  atendente_nome: string | null
  /** A conversa é de quem está vendo a tela: só ele responde. */
  atendente_e_voce: boolean
  pausado_ate: string | null
  ultima_em: string
  previa: string | null
}

export interface ListaDeConversas {
  /** `false`: o WhatsApi ainda não tem as rotas de conversa. */
  disponivel: boolean
  gateways: { id: string; nome: string }[]
  items: ConversaWhatsApp[]
  proximo: string | null
}

export type AutorDaMensagem = 'tecnico' | 'bot' | 'humano' | 'auto_reply' | 'api'

export interface MensagemDaConversa {
  id: string
  autor: AutorDaMensagem
  autor_nome: string | null
  texto: string
  em: string
  status: string | null
  /** Na mensagem do técnico: o que o bot decidiu (`bot_decisao`) e por quê. */
  decisao: string | null
  motivo: string | null
}

export interface PaginaDeMensagens {
  items: MensagemDaConversa[]
  proximo: string | null
  historico_incompleto: boolean
}

export interface ConversaAssumida {
  ref: string
  estado: EstadoDaConversa
  atendente_nome: string | null
  pausado_ate: string | null
}

const daConversa = (orgId: number, gwId: string, ref: string) =>
  `${raizDaOrg(orgId)}/conversas/${encodeURIComponent(gwId)}/${encodeURIComponent(ref)}`

export const whatsapiOrgApi = {
  organizacoes: (): Promise<OrgWhatsApi[]> =>
    api.get<{ items: OrgWhatsApi[] }>('/v1/whatsapi/organizacoes').then((r) => r.data.items),

  listMessages: (orgId: number, params?: WaMessageListParams): Promise<WaMessageListResponse> =>
    api.get<WaMessageListResponse>(`${raizDaOrg(orgId)}/messages`, { params }).then((r) => r.data),

  getMessage: (orgId: number, id: string): Promise<WaMessage> =>
    api.get<WaMessage>(`${raizDaOrg(orgId)}/messages/${id}`).then((r) => r.data),

  sendMessage: (orgId: number, payload: EnvioDaOrg): Promise<WaMessage> =>
    api.post<WaMessage>(`${raizDaOrg(orgId)}/messages`, payload).then((r) => r.data),

  chaves: (orgId: number): Promise<ChaveDaOrg[]> =>
    api.get<{ items: ChaveDaOrg[] }>(`${raizDaOrg(orgId)}/chaves`).then((r) => r.data.items),

  /** Todas as chaves da organização, para chegar ao KB de cada uma (`whatsapi:read`). */
  apiKeys: (orgId: number): Promise<ChaveDaOrg[]> =>
    api.get<{ items: ChaveDaOrg[] }>(`${raizDaOrg(orgId)}/api-keys`).then((r) => r.data.items),

  /** Conversas dos gateways da organização (`whatsapi:read`), pela última mensagem. */
  conversas: (orgId: number, params: { gateway?: string; antes_de?: string } = {}): Promise<ListaDeConversas> =>
    api.get<ListaDeConversas>(`${raizDaOrg(orgId)}/conversas`, { params }).then((r) => r.data),

  mensagensDaConversa: (orgId: number, gwId: string, ref: string, antesDe?: string): Promise<PaginaDeMensagens> =>
    api.get<PaginaDeMensagens>(`${daConversa(orgId, gwId, ref)}/mensagens`,
      { params: antesDe ? { antes_de: antesDe } : {} }).then((r) => r.data),

  /** Assumir, responder e devolver pedem `whatsapi:atender`; quem é o atendente vem da sessão. */
  assumirConversa: (orgId: number, gwId: string, ref: string): Promise<ConversaAssumida> =>
    api.post<ConversaAssumida>(`${daConversa(orgId, gwId, ref)}/assumir`).then((r) => r.data),

  responderConversa: (orgId: number, gwId: string, ref: string, texto: string) =>
    api.post<{ id: string; status: string; pausado_ate: string | null }>(
      `${daConversa(orgId, gwId, ref)}/responder`, { texto }).then((r) => r.data),

  devolverConversa: (orgId: number, gwId: string, ref: string) =>
    api.post<{ ref: string; estado: 'bot' }>(`${daConversa(orgId, gwId, ref)}/devolver`).then((r) => r.data),

  /** Overview, throughput, by-api-key e errors da organização. Não há rota de saúde. */
  stats: (orgId: number): EstatisticasWhatsApi => estatisticasEm(`${raizDaOrg(orgId)}/stats`),
}
